import classnames from 'classnames';
import React, { useEffect } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import Utils from '../utils';
import Auth0Util from '../utils/Auth0Util';
import Communication from '../utils/Communication';
import { CustomAuth0Error } from './CustomAuth0Error.component';
import ForgotPassword from './ForgotPassword.component';
import ForgotUsername from './ForgotUsername.component';
import { LoginError } from './LoginError.component';
import ResetPassword from './ResetPassword.component';
import { SignInForm } from './SignInForm.component';
import { SsoForm } from './SsoForm.component';

const {
	Session,
	Navigation,
	Util: { getVisitor, isInRoute, removeQueryParamByName },
	Logger: { logToServer },
	ThinClient: { isInThinClient },
	withLayoutAndModalContexts,
	Util,
} = Utils;

const onClickPrivacyLink = () => {
	const additionalInfo = isInThinClient()
		? {
				navType: 'newTab',
			}
		: {};

	logToServer({
		category: 'ui_interaction',
		action: 'click',
		ui_element: 'privacy_policy_link',
	});

	Navigation.navigateTo('privacyPolicy', additionalInfo);
};

const onClickDoNotSellLink = () => {
	const additionalInfo = isInThinClient()
		? {
				navType: 'newTab',
			}
		: {};

	logToServer({
		category: 'ui_interaction',
		action: 'click',
		ui_element: 'dont_sell_personal_information_link',
	});

	Navigation.navigateTo('doNotSell', additionalInfo);
};

const onClickLearningCenter = () => {
	const additionalInfo = {
		navType: 'newTab',
	};

	logToServer({
		category: 'ui_interaction',
		action: 'click',
		ui_element: 'learning_center_link',
	});

	Navigation.navigateTo('helpCenter', additionalInfo);
};

const onClickTrackAPackage = () => {
	const additionalInfo = {
		navType: 'newTab',
	};

	logToServer({
		category: 'ui_interaction',
		action: 'click',
		ui_element: 'track_a_package_link_signin',
	});

	Navigation.navigateTo('trackAPackage', additionalInfo);
};

const SignIn = ({
	SSOError,
	signInCallback,
	isInWebStore,
	layoutContext,
	modalContext,
	isAuth0Error = false,
	showAuth0CustomErrorPage = false,
}) => {
	const { isSso } = Session.getCalculatedSettings();
	const history = useHistory(); // Initialize useHistory hook

	const responsiveCls = classnames({
		tablet:
			!isInWebStore &&
			layoutContext.isMobile &&
			layoutContext.deviceType === 'tablet',
		mobile:
			isInWebStore ||
			(layoutContext.isMobile && layoutContext.deviceType === 'mobile'),
	});

	const appName = Util.getQueryParamByName('app_name');
	const isAuth0Winclient =
		appName && appName.toLowerCase().indexOf('winclient') !== -1;
	const isAuth0Mobile =
		appName && appName.toLowerCase().indexOf('mobile') !== -1;

	const showFooter = !isInWebStore && !isAuth0Winclient && !isAuth0Mobile;
	const logoClassName = isAuth0Winclient ? 'stamps-indigo-logo' : 'brand-logo';

	useEffect(() => {
		Session.signOut(() => {
			// hide all modals when SignIn component is rendered due to timeout
			modalContext.closeAll();

			logToServer({
				category: 'page_view',
				visitorId: getVisitor(),
			});
		});
	}, []);

	const handleForgotUsernameLinkClick = () => {
		history.push(`/recoverusername${window.location.search}`); // Navigate to the recoverUsername view
	};

	const sendConfirmationEmail = async username => {
		await Communication.resetPwdSendEmail({ username });
	};

	const goToSignInView = isSuccess => {
		const { UseAuth0SignIn } = Session.getSettings();
		if (isInRoute('resetpassword')) {
			if (isSuccess) {
				const username =
					Util.getQueryParamByName('username') ||
					Util.getCookie('print_username');

				// remove username and token from query params
				removeQueryParamByName(['username', 'token']);
				sendConfirmationEmail(username);
			}
		}

		history.push(`/${window.location.search}`); // Navigate to the sign-in view

		if (UseAuth0SignIn === true && !isInWebStore && !isSso) {
			Auth0Util.launchSignInPage();
		}
	};

	const renderSignIn = () => {
		if (isSso) {
			return <SsoForm responsiveCls={responsiveCls} SSOError={SSOError} />;
		}

		if (isAuth0Error) {
			return (
				<>
					<div className={logoClassName} />
					<LoginError />
				</>
			);
		}

		if (showAuth0CustomErrorPage) {
			return (
				<>
					<div className={logoClassName} />
					<CustomAuth0Error goToSignInView={goToSignInView} />
				</>
			);
		}

		return (
			<SignInForm
				responsiveCls={responsiveCls}
				modalContext={modalContext}
				signInCallback={signInCallback}
				isInWebStore={isInWebStore}
				isMobile={isInWebStore || layoutContext.deviceType === 'mobile'}
				history={history}
			/>
		);
	};

	return (
		<div
			className={`common-authentication authentication-signin ${responsiveCls}`}
		>
			<div className={`authentication-signin-wrapper ${responsiveCls}`}>
				<div className="form-container">
					<div className={`form-wrapper ${responsiveCls}`}>
						<Switch>
							<Route path="/resetpassword">
								<div className={logoClassName} />
								<ResetPassword goToSignInView={goToSignInView} />
							</Route>
							<Route path="/recoverpassword">
								<div className={logoClassName} />
								<ForgotPassword
									handleForgotUsernameLinkClick={handleForgotUsernameLinkClick}
									goToSignInView={goToSignInView}
									isAuth0Winclient={isAuth0Winclient}
									isAuth0Mobile={isAuth0Mobile}
								/>
							</Route>
							<Route path="/recoverusername">
								<div className={logoClassName} />
								<ForgotUsername goToSignInView={goToSignInView} />
							</Route>
							<Route path="/">
								<>{renderSignIn()}</>
							</Route>
						</Switch>
					</div>
				</div>
			</div>

			{showFooter && (
				<div className={`privacy-footer ${responsiveCls}`}>
					<div
						className={`privacy-links ${responsiveCls}`}
						onClick={onClickPrivacyLink}
						role="link"
						tabIndex={0}
					>
						Privacy Policy
					</div>
					{layoutContext.deviceType !== 'mobile' && (
						<div className="link-separator">|</div>
					)}
					<div
						className={`privacy-links ${responsiveCls}`}
						onClick={onClickDoNotSellLink}
						role="link"
						tabIndex={0}
					>
						Do Not Sell My Personal Information
					</div>
					<>
						{layoutContext.deviceType !== 'mobile' && (
							<div className="link-separator">|</div>
						)}
						<div
							className={`privacy-links ${responsiveCls}`}
							onClick={onClickLearningCenter}
							role="link"
							tabIndex={0}
						>
							Help Center
						</div>
						{layoutContext.deviceType !== 'mobile' && (
							<div className="link-separator">|</div>
						)}
						<div
							className={`privacy-links ${responsiveCls}`}
							onClick={onClickTrackAPackage}
							role="link"
							tabIndex={0}
						>
							Track a Package
						</div>
					</>
				</div>
			)}
		</div>
	);
};

export default withLayoutAndModalContexts(SignIn);
